import { useState } from 'react';
import { Box, Button, IconButton, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { IRAN_MOBILE_REGEX, NATIONAL_ID_REGEX } from '../../constants/regex';
import IResponse from '../../interfaces/response';
import axios from 'axios';
import { IAuthResponse } from '../../interfaces/user';
import { Link, useNavigate } from 'react-router-dom';

const registerSchema = z
	.object({
		emailOrPhone: z.string().refine(value => z.string().email().safeParse(value).success || IRAN_MOBILE_REGEX.test(value), { message: 'invalid_email_or_phone_message' }),
		nationCode: z.string().regex(NATIONAL_ID_REGEX, { message: 'invalid_national_identification_number_message' }),
		company: z.string().min(1, 'invalid_company_message'),
		password: z.string().min(6, 'invalid_password_message'),
		repeatPassword: z.string().min(6, 'invalid_password_message'),
	})
	.refine(data => data.password === data.repeatPassword, {
		path: ['repeatPassword'],
		message: 'passwords_do_not_match_message',
	});

const Register = () => {
	const [showPassword, setShowPassword] = useState(false);
	const [showRepeatPassword, setShowRepeatPassword] = useState(false);
	const { t } = useTranslation();
	const navigate = useNavigate();

	const {
		register,
		handleSubmit,
		formState: { errors },
	} = useForm<z.infer<typeof registerSchema>>({
		resolver: zodResolver(registerSchema),
	});

	const toggleShowPassword = () => {
		setShowPassword(old => !old);
	};

	const toggleShowRepeatPassword = () => {
		setShowRepeatPassword(old => !old);
	};

	const handleRegister = (data: z.infer<typeof registerSchema>) => {
		axios<IResponse<IAuthResponse>>({
			url: '/users/register',
			method: 'POST',
			data: {
				login: data?.emailOrPhone,
				nation_code: data?.nationCode,
				company_title: data?.company,
				password: data?.password,
			},
		}).then(res => {
			if (res.data.success) {
				const token = res.data.data.token;
				localStorage.setItem('token', token);
				axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
				navigate('/');
			}
		});
	};

	return (
		<>
			<Box>
				<form className="flex w-full flex-col gap-y-5" onSubmit={handleSubmit(handleRegister)}>
					<TextField className="w-full" id="outlined-required" label={t('email-input-placeholder')} {...register('emailOrPhone')} error={!!errors.emailOrPhone} helperText={t(errors.emailOrPhone?.message!)} />
					<TextField className="w-full" id="outlined-required" label={t('nation-code')} {...register('nationCode')} error={!!errors.nationCode} helperText={t(errors.nationCode?.message!)} />
					<TextField className="w-full" id="outlined-required" label={t('company_name')} {...register('company')} error={!!errors.company} helperText={t(errors.company?.message!)} />
					<TextField
						InputProps={{
							endAdornment: (
								<IconButton onClick={toggleShowPassword} className="text-text-secondary">
									{showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
								</IconButton>
							),
						}}
						className="w-full"
						id="outlined-password-input"
						label={t('password')}
						type={showPassword ? 'text' : 'password'}
						autoComplete="current-password"
						{...register('password')}
						error={!!errors.password}
						helperText={t(errors.password?.message!)}
					/>
					<TextField
						InputProps={{
							endAdornment: (
								<IconButton onClick={toggleShowRepeatPassword} className="text-text-secondary">
									{showRepeatPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
								</IconButton>
							),
						}}
						className="w-full"
						id="outlined-password-input"
						label={t('repeat-password')}
						type={showPassword ? 'text' : 'password'}
						autoComplete="current-password"
						{...register('repeatPassword')}
						error={!!errors.repeatPassword}
						helperText={t(errors.repeatPassword?.message!)}
					/>
					<Button type="submit" size="large" variant="contained" className="text-base font-normal">
						{t('sign_up')}
					</Button>
				</form>
			</Box>
			<Box className="mt-2 flex justify-between text-primary-main">
				<Link to={'/auth/login'}>{t('login')}</Link>
			</Box>
		</>
	);
};

export default Register;
